import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { documentAPI } from '../../utils/crmApi';
import {
    DocumentTextIcon,
    PlusIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon,
    ArrowDownTrayIcon,
    ArrowUpTrayIcon,
    ShareIcon,
    LockClosedIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    DocumentDuplicateIcon,
    FolderIcon,
    TagIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useDialog, ConfirmationDialog } from '../../components/kibo-ui/dialog';
import {
    TableProvider,
    Table,
    TableHeader,
    TableSearch,
    TableHead,
    TableBody,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableFooter,
    StatusBadge,
    TableAvatar
} from '../../components/kibo-ui/table';

const Documents = () => {
    const navigate = useNavigate();
    const { openDialog } = useDialog();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20 });
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Document status colors
    const getStatusColor = (status) => {
        const colors = {
            'draft': 'bg-muted text-foreground',
            'pending_review': 'bg-yellow-100 text-yellow-700',
            'approved': 'bg-green-100 text-green-700',
            'signed': 'bg-blue-100 text-blue-700',
            'expired': 'bg-red-100 text-red-700',
            'archived': 'bg-muted text-foreground'
        };
        return colors[status] || 'bg-muted text-foreground';
    };

    // Document type icons
    const getTypeIcon = (type) => {
        const icons = {
            'contract': '📄',
            'agreement': '🤝',
            'application': '📝',
            'certificate': '🏆',
            'invoice': '🧾',
            'receipt': '🧾',
            'passport': '📘',
            'visa': '🛂',
            'insurance': '🛡️',
            'lease': '🏠',
            'employment': '💼',
            'medical': '🏥',
            'educational': '🎓',
            'financial': '💰',
            'other': '📎'
        };
        return icons[type] || '📄';
    };

    useEffect(() => {
        loadDocuments();
    }, [search, typeFilter, statusFilter, categoryFilter, pagination.page]);

    const loadDocuments = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(search && { search }),
                ...(typeFilter && { type: typeFilter }),
                ...(statusFilter && { status: statusFilter }),
                ...(categoryFilter && { category: categoryFilter })
            };
            
            const response = await documentAPI.getAll(params);
            if (response.data.success) {
                setDocuments(response.data.data);
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            }
        } catch (error) {
            console.error('Failed to load documents:', error);
            toast.error('Failed to load documents');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectDocument = (documentId) => {
        setSelectedDocuments(prev => 
            prev.includes(documentId) 
                ? prev.filter(id => id !== documentId)
                : [...prev, documentId]
        );
    };

    const handleSelectAll = () => {
        if (selectedDocuments.length === documents.length) {
            setSelectedDocuments([]);
        } else {
            setSelectedDocuments(documents.map(doc => doc._id));
        }
    };

    const handleBulkAction = (action) => {
        if (selectedDocuments.length === 0) return;
        
        const actionMessages = {
            'download': 'download',
            'archive': 'archive',
            'delete': 'delete',
            'share': 'share'
        };

        openDialog({
            title: `${actionMessages[action]} Documents`,
            description: `Are you sure you want to ${actionMessages[action]} ${selectedDocuments.length} document(s)?`,
            confirmText: actionMessages[action].charAt(0).toUpperCase() + actionMessages[action].slice(1),
            cancelText: 'Cancel',
            variant: action === 'delete' ? 'danger' : 'warning',
            onConfirm: async () => {
                try {
                    setBulkActionLoading(true);
                    await documentAPI.bulkAction(selectedDocuments, action);
                    toast.success(`${selectedDocuments.length} document(s) ${actionMessages[action]}ed successfully`);
                    setSelectedDocuments([]);
                    loadDocuments();
                } catch (error) {
                    console.error('Error performing bulk action:', error);
                    toast.error('Failed to perform bulk action');
                } finally {
                    setBulkActionLoading(false);
                }
            }
        });
    };

    const handleDeleteDocument = (documentId) => {
        openDialog({
            title: 'Delete Document',
            description: 'Are you sure you want to delete this document? This action cannot be undone.',
            confirmText: 'Delete Document',
            cancelText: 'Cancel',
            variant: 'danger',
            onConfirm: async () => {
                try {
                    await documentAPI.delete(documentId);
                    toast.success('Document deleted successfully');
                    loadDocuments();
                } catch (error) {
                    console.error('Error deleting document:', error);
                    toast.error('Failed to delete document');
                }
            }
        });
    };

    const handleFileUpload = async (event) => {
        const files = event.target.files;
        if (!files.length) return;

        try {
            setUploading(true);
            const formData = new FormData();
            
            for (let i = 0; i < files.length; i++) {
                formData.append('files', files[i]);
            }

            await documentAPI.upload(formData);
            toast.success(`${files.length} file(s) uploaded successfully`);
            loadDocuments();
        } catch (error) {
            console.error('Error uploading files:', error);
            toast.error('Failed to upload files');
        } finally {
            setUploading(false);
            event.target.value = ''; // Reset file input
        }
    };

    const handleDownloadDocument = async (documentId) => {
        try {
            const response = await documentAPI.download(documentId);
            // Create blob and download
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `document-${documentId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading document:', error);
            toast.error('Failed to download document');
        }
    };

    const columns = [
        { accessor: 'select', label: '', sortable: false, width: 'w-12' },
        { accessor: 'name', label: 'Document', sortable: true },
        { accessor: 'type', label: 'Type', sortable: true },
        { accessor: 'status', label: 'Status', sortable: true },
        { accessor: 'client', label: 'Client', sortable: true },
        { accessor: 'size', label: 'Size', sortable: true },
        { accessor: 'modified', label: 'Modified', sortable: true },
        { accessor: 'actions', label: 'Actions', sortable: false }
    ];

    const typeOptions = [
        { value: '', label: 'All Types' },
        { value: 'contract', label: 'Contract' },
        { value: 'agreement', label: 'Agreement' },
        { value: 'application', label: 'Application' },
        { value: 'certificate', label: 'Certificate' },
        { value: 'invoice', label: 'Invoice' },
        { value: 'passport', label: 'Passport' },
        { value: 'visa', label: 'Visa' },
        { value: 'insurance', label: 'Insurance' },
        { value: 'lease', label: 'Lease' },
        { value: 'employment', label: 'Employment' },
        { value: 'medical', label: 'Medical' },
        { value: 'educational', label: 'Educational' },
        { value: 'financial', label: 'Financial' },
        { value: 'other', label: 'Other' }
    ];

    const statusOptions = [
        { value: '', label: 'All Statuses' },
        { value: 'draft', label: 'Draft' },
        { value: 'pending_review', label: 'Pending Review' },
        { value: 'approved', label: 'Approved' },
        { value: 'signed', label: 'Signed' },
        { value: 'expired', label: 'Expired' },
        { value: 'archived', label: 'Archived' }
    ];

    const categoryOptions = [
        { value: '', label: 'All Categories' },
        { value: 'legal', label: 'Legal' },
        { value: 'immigration', label: 'Immigration' },
        { value: 'financial', label: 'Financial' },
        { value: 'personal', label: 'Personal' },
        { value: 'business', label: 'Business' },
        { value: 'medical', label: 'Medical' },
        { value: 'educational', label: 'Educational' },
        { value: 'other', label: 'Other' }
    ];

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };
        
        return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                            <div>
                    <h1 className="text-2xl font-bold text-foreground">Document Management</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Manage client documents, contracts, and legal paperwork
                    </p>
                            </div>
                <div className="mt-4 sm:mt-0 flex items-center gap-3">
                    {selectedDocuments.length > 0 && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleBulkAction('download')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                            >
                                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                                Download
                            </button>
                            <button
                                onClick={() => handleBulkAction('share')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50"
                            >
                                <ShareIcon className="h-4 w-4 mr-1" />
                                Share
                            </button>
                                <button
                                onClick={() => handleBulkAction('archive')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-foreground bg-muted border border-border rounded-lg hover:bg-muted/80 disabled:opacity-50"
                                >
                                <FolderIcon className="h-4 w-4 mr-1" />
                                Archive
                                </button>
                                <button
                                onClick={() => handleBulkAction('delete')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                                >
                                <TrashIcon className="h-4 w-4 mr-1" />
                                Delete
                                </button>
                            </div>
                    )}
                    
                    {/* File Upload */}
                    <label className="inline-flex items-center px-4 py-2 border border-border rounded-lg shadow-sm text-sm font-medium text-foreground bg-background hover:bg-muted cursor-pointer">
                        <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                        Upload Files
                        <input
                            type="file"
                            multiple
                            onChange={handleFileUpload}
                            disabled={uploading}
                            className="hidden"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                        />
                    </label>
                    
                    <Link
                        to="/documents/new"
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                    >
                        <PlusIcon className="h-5 w-5 mr-2" />
                        Create Document
                    </Link>
                </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <DocumentTextIcon className="h-8 w-8 text-blue-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-muted-foreground">Total Documents</p>
                            <p className="text-2xl font-semibold text-foreground">{documents.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <CheckCircleIcon className="h-8 w-8 text-green-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-muted-foreground">Signed</p>
                            <p className="text-2xl font-semibold text-foreground">
                                {documents.filter(d => d.status === 'signed').length}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <ClockIcon className="h-8 w-8 text-yellow-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-muted-foreground">Pending Review</p>
                            <p className="text-2xl font-semibold text-foreground">
                                {documents.filter(d => d.status === 'pending_review').length}
                            </p>
                            </div>
                    </div>
                        </div>
                <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-muted-foreground">Expired</p>
                            <p className="text-2xl font-semibold text-foreground">
                                {documents.filter(d => d.status === 'expired').length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Type</label>
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="w-full border border-input rounded-lg px-3 py-2 text-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        >
                            {typeOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Status</label>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full border border-input rounded-lg px-3 py-2 text-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                    >
                        {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Category</label>
                    <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="w-full border border-input rounded-lg px-3 py-2 text-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        >
                            {categoryOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    </div>
                    <div className="flex items-end">
                    <button
                            onClick={() => {
                                setTypeFilter('');
                                setStatusFilter('');
                                setCategoryFilter('');
                                setSearch('');
                            }}
                            className="w-full px-4 py-2 text-sm font-medium text-foreground bg-muted border border-border rounded-lg hover:bg-muted/80"
                        >
                            Clear Filters
                    </button>
                </div>
                </div>
            </div>

            {/* Enhanced Table with Kibo UI */}
            <TableProvider 
                data={documents} 
                columns={columns}
                onSort={(key, direction) => console.log('🔄 Table sort:', { key, direction })}
                onFilter={(key, value) => console.log('🔍 Table filter:', { key, value })}
            >
                <Table>
                    <TableHeader>
                        <TableSearch placeholder="Search documents by name, client, or content..." />
                    </TableHeader>
                    
                    <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                        <table className="min-w-full divide-y divide-border">
                            <TableHead>
                                <tr>
                                    {columns.map((column) => (
                                        <TableHeaderCell key={column.accessor} column={column}>
                                            {column.accessor === 'select' ? (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedDocuments.length === documents.length && documents.length > 0}
                                                    onChange={handleSelectAll}
                                                    className="h-4 w-4 text-primary focus:ring-primary border-input rounded"
                                                />
                                            ) : (
                                                column.label
                                            )}
                                        </TableHeaderCell>
                                    ))}
                                </tr>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-12 text-center">
                                            <div className="flex items-center justify-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                                        </td>
                                    </tr>
                                ) : documents.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-12 text-center">
                                            <div className="text-center">
                                                <DocumentTextIcon className="mx-auto h-12 w-12 text-muted-foreground" />
                                                <h3 className="mt-2 text-sm font-medium text-foreground">No documents found</h3>
                                                <p className="mt-1 text-sm text-muted-foreground">
                                                    Get started by uploading your first document.
                                                </p>
                                                <div className="mt-4 flex items-center justify-center gap-3">
                                                    <label className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer">
                                                        <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                                                        Upload Files
                                                        <input
                                                            type="file"
                                                            multiple
                                                            onChange={handleFileUpload}
                                                            disabled={uploading}
                                                            className="hidden"
                                                            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                                                        />
                                                    </label>
                                                    <Link
                                                        to="/documents/new"
                                                        className="inline-flex items-center px-4 py-2 border border-border rounded-lg shadow-sm text-sm font-medium text-foreground bg-background hover:bg-muted"
                                                    >
                                                        <PlusIcon className="h-5 w-5 mr-2" />
                                                        Create Document
                                                    </Link>
                        </div>
                    </div>
                                        </td>
                                    </tr>
                                ) : (
                                    documents.map((document) => (
                                        <TableRow 
                                            key={document._id}
                                            onClick={() => navigate(`/documents/${document._id}`)}
                                            className="cursor-pointer"
                                        >
                                            <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedDocuments.includes(document._id)}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectDocument(document._id);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="h-4 w-4 text-primary focus:ring-primary border-input rounded"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center">
                                                    <div className="text-2xl mr-3">{getTypeIcon(document.type)}</div>
                                                    <div>
                                                        <span
                                                            className="text-sm font-medium text-foreground"
                                                        >
                                                            {document.name}
                                                        </span>
                                                        <div className="text-xs text-muted-foreground">
                                                            {document.description}
                                                        </div>
                                                        {document.tags && document.tags.length > 0 && (
                                                            <div className="flex flex-wrap gap-1 mt-1">
                                                                {document.tags.slice(0, 2).map(tag => (
                                                                    <span key={tag} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-muted text-foreground">
                                                                        <TagIcon className="h-3 w-3 mr-1" />
                                                                        {tag}
                                                                    </span>
                                                                ))}
                                                                {document.tags.length > 2 && (
                                                                    <span className="text-xs text-muted-foreground">+{document.tags.length - 2} more</span>
                                                                )}
                </div>
            )}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {document.type.replace('_', ' ')}
                        </span>
                                            </TableCell>
                                            <TableCell>
                                                <StatusBadge 
                                                    status={document.status}
                                                    className={getStatusColor(document.status)}
                                                >
                                                    {document.status.replace('_', ' ')}
                                                </StatusBadge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm text-foreground">
                                                    {document.clientName || 'No client'}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {document.category}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm text-foreground">
                                                    {formatFileSize(document.fileSize)}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {document.fileType}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm text-foreground">
                                                    {new Date(document.updatedAt).toLocaleDateString()}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {new Date(document.updatedAt).toLocaleTimeString()}
                                                </div>
                                            </TableCell>
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                            <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDownloadDocument(document._id);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                                        title="Download"
                            >
                                                        <ArrowDownTrayIcon className="h-4 w-4" />
                            </button>
                                                    <Link
                                                        to={`/documents/${document._id}`}
                                                        className="text-muted-foreground hover:text-foreground text-sm font-medium"
                                                        title="View"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <EyeIcon className="h-4 w-4" />
                                                    </Link>
                                                    <Link
                                                        to={`/documents/${document._id}/edit`}
                                                        className="text-muted-foreground hover:text-foreground text-sm font-medium"
                                                        title="Edit"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <PencilIcon className="h-4 w-4" />
                                                    </Link>
                            <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteDocument(document._id);
                                                        }}
                                                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                                                        title="Delete"
                            >
                                                        <TrashIcon className="h-4 w-4" />
                            </button>
                        </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </table>
                    </div>
                    
                    <TableFooter>
                        <div className="px-6 py-3 border-t border-border">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-muted-foreground">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                    </div>
                                <div className="flex items-center gap-2">
                        <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page === 1}
                                        className="px-3 py-1 text-sm text-foreground bg-background border border-border rounded hover:bg-muted disabled:opacity-50"
                        >
                                        Previous
                        </button>
                                    <span className="px-3 py-1 text-sm text-muted-foreground">
                                        Page {pagination.page} of {pagination.pages}
                                    </span>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page === pagination.pages}
                                        className="px-3 py-1 text-sm text-foreground bg-background border border-border rounded hover:bg-muted disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </TableFooter>
                </Table>
            </TableProvider>
        </div>
    );
};

export default Documents;